import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { films } from "@/mocks/films";

interface SearchOverlayProps {
  onClose: () => void;
}

export default function SearchOverlay({ onClose }: SearchOverlayProps) {
  const [keyword, setKeyword] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const results = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return [];
    return films.filter(
      (film) =>
        film.title.toLowerCase().includes(q) ||
        film.director.toLowerCase().includes(q) ||
        film.genres.some((g) => g.toLowerCase().includes(q)),
    );
  }, [keyword]);

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-24 pb-10 bg-background-50/85 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-2xl bg-background-100 rounded-lg border border-background-200/70 overflow-hidden animate-fade-up">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-background-200/70">
          <i className="ri-search-line text-xl text-foreground-500" />
          <input
            ref={inputRef}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索影片标题、导演或类型…"
            className="flex-1 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 outline-none"
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭搜索"
            className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-200/60 hover:text-foreground-950 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="max-h-[52vh] overflow-y-auto">
          {keyword.trim() === "" && (
            <p className="px-5 py-8 text-sm text-foreground-500 text-center">
              输入关键词，快速找到你想看的球幕影片
            </p>
          )}
          {keyword.trim() !== "" && results.length === 0 && (
            <p className="px-5 py-8 text-sm text-foreground-500 text-center">
              没有找到与「{keyword}」匹配的影片，换个关键词试试
            </p>
          )}
          {results.map((film) => (
            <Link
              key={film.id}
              to={`/films/${film.id}`}
              className="flex items-center gap-4 px-5 py-3 hover:bg-background-200/60 transition-colors cursor-pointer"
            >
              <div className="w-12 h-16 rounded-md overflow-hidden bg-background-200 shrink-0">
                <img src={film.poster} alt={film.title} className="w-full h-full object-cover object-top" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground-950 truncate">{film.title}</p>
                <p className="text-xs text-foreground-500 truncate">
                  {film.genres.join(" / ")} · {film.year} · {film.rating.toFixed(1)} 分
                </p>
              </div>
              <i className="ri-arrow-right-s-line text-lg text-foreground-500 ml-auto" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}